import classnames from 'classnames';
import hoistNonReactStatics from 'hoist-non-react-statics';
import React from 'react';
import Form from '../../form';
import { TFormVariant } from '../../form/Form.types';
import InputSkeleton, { InputSkeletonProps } from '../components/input-skeleton';
import { fireInputChange } from '../inputs.utils';
import { InputAreaSC, InputSkeletonSC } from './text-field.styled';

const textareaLineHeight = 20;

type TSkeletonProps = Pick<InputSkeletonProps, 'left' | 'right'>;

interface InputProps
  extends React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLTextAreaElement>,
      HTMLTextAreaElement
    >,
    TSkeletonProps {
  clearable?: boolean;
  variant?: TFormVariant;
  /**
   * Input states
   */
  disabled?: boolean;
  readonly?: boolean;
  error?: boolean;
  borderLess?: InputSkeletonProps['borderLess'];
  style?: React.CSSProperties;
  className?: string;
  autoSize?: {
    minRows?: number;
    maxRows?: number;
  };
}

const TextArea = (props: InputProps, ref?: React.Ref<HTMLTextAreaElement | null>) => {
  const {
    style,
    clearable,
    disabled,
    readonly,
    error,
    className,
    left,
    right,
    borderLess,
    type,
    variant,
    required,
    onChange,
    autoSize,
    ...rest
  } = props;

  const settings = Form.useFormSettings({ variant, disabled });

  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  React.useImperativeHandle(ref, () => inputRef.current);

  const [focused, setFocused] = React.useState(false);

  const hasValue =
    (props.value && typeof props.value === 'string') || Array.isArray(props.value)
      ? props.value.length > 0
      : true;

  const onFocus: React.FocusEventHandler<HTMLTextAreaElement> = (e) => {
    props.onFocus?.(e);
    setFocused(true);
  };

  const onBlur: React.FocusEventHandler<HTMLTextAreaElement> = (e) => {
    props.onBlur?.(e);
    setFocused(false);
  };

  const onClick = () => {
    inputRef.current?.focus();
  };

  return (
    <InputSkeletonSC
      focused={focused}
      onClick={onClick}
      style={style}
      error={error}
      readonly={readonly}
      className={classnames('ui-textarea', className)}
      borderLess={borderLess}
      variant={settings.variant}
      disabled={settings.disabled}
      left={left}
      right={
        clearable && hasValue && !disabled && !readonly ? (
          <InputSkeleton.ClearAction
            variant={settings.variant}
            onClearPress={() => fireInputChange(inputRef.current, 'input', '')}
            right={right}
          />
        ) : (
          right
        )
      }
    >
      <InputAreaSC
        {...rest}
        ref={inputRef}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        onChange={onChange}
        readOnly={readonly}
        minRows={autoSize?.minRows}
        maxRows={autoSize?.maxRows}
        variant={settings.variant}
      />
    </InputSkeletonSC>
  );
};

TextArea.Prefix = InputSkeleton.Prefix;
TextArea.Suffix = InputSkeleton.Suffix;
TextArea.Action = InputSkeleton.Action;
TextArea.ClearAction = InputSkeleton.ClearAction;
TextArea.Icon = InputSkeleton.Icon;

export default hoistNonReactStatics(React.forwardRef(TextArea), TextArea) as typeof TextArea;
